import { useEffect, useRef, useCallback } from "react";

interface UseDebouncedSearchProps {
  enabled: boolean;
  delay: number;
  onSearch: (value: string, field?: string) => void;
}

export function useDebouncedSearch({
  enabled,
  delay,
  onSearch
}: UseDebouncedSearchProps) {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedSearch = useCallback((value: string, field?: string) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!enabled) {
      // If debouncing not enabled, call immediately
      onSearch(value, field);
      return;
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      onSearch(value, field);
    }, delay);
  }, [enabled, delay, onSearch]);

  return debouncedSearch;
}

